import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {Tag} from 'folder-items/FileConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {MetadataTableHeaderReadonlyContent} from 'metadata/MetadataTableHeader';
import MetadataTagTable from 'metadata/MetadataTagTable';
import notifications from 'notifications';
import * as StudyDAO from 'studies/StudyDAO';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  StudySchemaTagTableQuery,
  StudySchemaTagTableQueryVariables,
} from './__generated/StudySchemaTagTableQuery';

export const STUDY_SCHEMA_TAG_TABLE_GRAPHQL_QUERY = gql`
  query StudySchemaTagTableQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $id) {
      ... on StudyNode {
        id
        tags: tagsJson
        name
        studySchema {
          id
          name
        }
        permissions {
          writable
        }
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

export default function StudySchemaTagTable({studyId}: {studyId: string}): JSX.Element {
  const isMounted = useIsNotUnmounted();

  return (
    <BenchlingQuery<StudySchemaTagTableQuery, StudySchemaTagTableQueryVariables>
      query={STUDY_SCHEMA_TAG_TABLE_GRAPHQL_QUERY}
      variables={{id: studyId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      fetchPolicy="cache-and-network"
      renderCachedDataWhenRefetching={true}
    >
      {({study}, {refetch}) =>
        isInaccessible(study) ? null : (
          <MetadataTagTable
            title="Schema"
            subheaderNode={<MetadataTableHeaderReadonlyContent text={study.studySchema.name} />}
            emptyMessage="This study has no fields."
            fileId={study.id}
            initialTags={study.tags}
            areTagsEditable={study.permissions.writable}
            showRequiredFieldErrors={true}
            updateTags={async (inputData: TagUpdateOptions, param?: {onFailure: () => void}) => {
              try {
                await StudyDAO.updateStudy(study.id, {tags: inputData.toSet});
                // If there was a failure, updateStudy will show an error toast and not continue. Show a
                // success toast if we get here regardless of whether the metadata table is still mounted.
                notifications.success('Fields were updated successfully.');
                const refetchResult = await refetch();
                if (isMounted.current) {
                  // The following will only run if the metadata table is still mounted.
                  const {data} = refetchResult;
                  assert(!isInaccessible(data.study), 'Expected study to be accessible');
                  return [{id: study.id, tags: data.study.tags as Array<Tag>}];
                } else {
                  return null;
                }
              } catch (e) {
                // If we have an unexpected failure (either JS crash or network error), we re-throw to
                // let the app's default handler do the right thing (notify user and maybe sent to Sentry).
                // However, we first call the onFailure handle to ensure that the MetadataTagTable preserves
                // edit mode.
                param?.onFailure?.();
                throw e;
              }
            }}
          />
        )
      }
    </BenchlingQuery>
  );
}
